import { getNodeSize } from "@features/builder/graph/nodes/NodeGroupResizable/getNodeSize"
import css from "@features/builder/graph/nodes/NodeGroupResizable/NodeGroupResizable.module.css"
import { AppNode, GroupNode } from "@features/builder/graph/types"
import {
  expandedGroupMinHeight,
  expandedGroupMinWidth,
  groupPadding,
  groupTitleHeight,
} from "@features/builder/helpers/nodes"
import { nodesGroupMapper } from "@features/builder/helpers/nodesGroupMapper"
import { NodeResizeControl, OnResizeEnd, ShouldResize, useReactFlow } from "@xyflow/react"
import { OnResizeStart, ResizeControlVariant } from "@xyflow/system"
import { useRef } from "react"

export const NodeResizeTop = ({ node }: { node: GroupNode }) => {
  const { getNodes, getInternalNode, updateNode } = useReactFlow<AppNode>()
  const constrainedSizeRef = useRef<{ width: number; height: number; x: number; y: number } | null>(
    null,
  )

  const handleResizeStart: OnResizeStart = (_event, _p) => {
    const mapper = nodesGroupMapper(getNodes())
    const parents = mapper.childToParentGroupsIdsMapDeep.get(node.id) ?? []
    //  @TODO (Bartek Laskowski) 2025-08-19: do not extend due to bug that messing x and y of parents
    const expandParent = false
    for (const parentId of parents) {
      updateNode(parentId, {
        expandParent,
      })
    }
    updateNode(node.id, {
      expandParent,
    })
  }

  const handleResizeEnd: OnResizeEnd = (_event, { width, height }) => {
    const mapper = nodesGroupMapper(getNodes())
    const parents = mapper.childToParentGroupsIdsMapDeep.get(node.id) ?? []

    const finalWidth = constrainedSizeRef.current?.width ?? width
    const finalHeight = constrainedSizeRef.current?.height ?? height
    constrainedSizeRef.current = null

    for (const parentId of parents) {
      const parentNode = mapper.groupsMap.get(parentId)

      const deepthFromResized = parents.length + 1 - Number(parentNode?.data.nestingLevel)
      const { width, height } = getNodeSize(parentNode, deepthFromResized)

      updateNode(parentId, {
        expandParent: false,
        style: { width, height },
      })
    }
    updateNode(node.id, {
      expandParent: false,
      style: { width: finalWidth, height: finalHeight },
    })
  }

  const handleShouldResize: ShouldResize = (_event, resizedNode) => {
    const internalResizedNode = getInternalNode(node.id)

    const resizedNodeAbs = {
      x: Number(internalResizedNode?.internals.positionAbsolute?.x),
      y: Number(internalResizedNode?.internals.positionAbsolute?.y),
      width: Number(internalResizedNode?.width),
      height: Number(internalResizedNode?.height),
    }

    let constrainedWidth = resizedNode.width
    let constrainedHeight = resizedNode.height
    let constrainedX = resizedNode.x
    let constrainedY = resizedNode.y

    const groupBoundriesAbs = {
      left: resizedNodeAbs.x + groupPadding,
      right: resizedNodeAbs.x + resizedNode.width - groupPadding,
      top: resizedNodeAbs.y + groupPadding + groupTitleHeight,
      bottom: resizedNodeAbs.y + resizedNode.height - groupPadding,
    }

    const mapper = nodesGroupMapper(getNodes())
    const subGroups = mapper.parentToChildGroupsIdsMap.get(node.id) ?? []
    const subNodes = mapper.groupToNodesIdsMap.get(node.id) ?? []

    // SUB-NODES and SUB-GROUPS
    for (const childNodeId of [...subNodes, ...subGroups]) {
      const internalNode = getInternalNode(childNodeId)
      const childNode = mapper.allMap.get(childNodeId)

      const child = {
        x: Number(internalNode?.internals.positionAbsolute.x),
        y: Number(internalNode?.internals.positionAbsolute.y),
        width: Number(childNode?.measured?.width),
        height: Number(childNode?.measured?.height),
      }

      const childBoundriesAbs = {
        left: child.x,
        right: child.x + child.width,
        top: child.y,
        bottom: child.y + child.height,
      }

      const dontResize = groupBoundriesAbs.top >= childBoundriesAbs.top

      if (dontResize) {
        const minWidthForChild = child.x - resizedNodeAbs.x + child.width + groupPadding + 5
        const minHeightForChild =
          child.y - resizedNodeAbs.y + child.height + groupTitleHeight + groupPadding - 12

        // right side
        constrainedWidth = Math.max(constrainedWidth, minWidthForChild)
        // bottom side
        constrainedHeight = Math.max(constrainedHeight, minHeightForChild)
        // left side
        constrainedX = Math.min(constrainedX, resizedNodeAbs.x)
        // top side
        constrainedY = Math.min(constrainedY, resizedNodeAbs.y)
      }
    }

    constrainedSizeRef.current = {
      width: constrainedWidth,
      height: constrainedHeight,
      x: constrainedX,
      y: constrainedY,
    }

    return (
      constrainedWidth === resizedNode.width &&
      constrainedHeight === resizedNode.height &&
      constrainedX === resizedNode.x &&
      constrainedY === resizedNode.y
    )
  }

  return (
    <NodeResizeControl
      position="top"
      className={css.resizeLine}
      variant={ResizeControlVariant.Line}
      minWidth={expandedGroupMinWidth}
      minHeight={expandedGroupMinHeight}
      shouldResize={handleShouldResize}
      autoScale={true}
      onResizeStart={handleResizeStart}
      onResizeEnd={handleResizeEnd}
    />
  )
}
